// Shared PAR Intelligence drawer controller.
// Mounting this provider in BentoShell lets any component — nav header, insight
// cards, or future surfaces — open the drawer with or without a pre-loaded workflow.

import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import type {ActionWorkflowDefinition} from "./components/intelligence/workflowModels";
import type {AgentIdentity} from "./insightAgents";
import ParIntelligenceDrawer from "./components/intelligence/ParIntelligenceDrawer";

export type IntelligenceMode = "demo" | "live";

const MODE_STORAGE_KEY = "parIntelligenceMode";
const OPEN_ON_LOAD_STORAGE_KEY = "parIntelligenceOpenOnLoad";

/** Call right before navigating to the landing page so the drawer opens
 * automatically once BentoShell (and this provider) mounts there. */
export const requestIntelligenceDrawerOnLoad = (): void => {
    if (typeof window === "undefined") {
        return;
    }
    window.sessionStorage.setItem(OPEN_ON_LOAD_STORAGE_KEY, "1");
};

const consumeIntelligenceDrawerOnLoadRequest = (): boolean => {
    if (typeof window === "undefined") {
        return false;
    }
    const shouldOpen =
        window.sessionStorage.getItem(OPEN_ON_LOAD_STORAGE_KEY) === "1";
    if (shouldOpen) {
        window.sessionStorage.removeItem(OPEN_ON_LOAD_STORAGE_KEY);
    }
    return shouldOpen;
};

const readStoredMode = (): IntelligenceMode => {
    if (typeof window === "undefined") {
        return "live";
    }
    // Live is the default; only an explicit stored "demo" opts out of it.
    return window.localStorage.getItem(MODE_STORAGE_KEY) === "demo"
        ? "demo"
        : "live";
};

interface BentoIntelligenceContextValue {
    /** Open the drawer on the default chat view, optionally with a guiding agent. */
    openDrawer: (agent?: AgentIdentity) => void;
    /** Open the drawer and immediately load a workflow composer. */
    openWorkflow: (definition: ActionWorkflowDefinition) => void;
    /** Open the drawer on chat and immediately send a prompt. */
    openWithPrompt: (prompt: string, agent?: AgentIdentity) => void;
    closeDrawer: () => void;
    isOpen: boolean;
    /** Demo = canned replies; Live = call the Coach AI intelligence endpoint. */
    mode: IntelligenceMode;
    setMode: (mode: IntelligenceMode) => void;
}

const BentoIntelligenceContext = createContext<BentoIntelligenceContextValue | null>(
    null
);

interface ProviderProps {
    children: ReactNode;
}

export const BentoIntelligenceProvider: FC<ProviderProps> = ({children}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeWorkflow, setActiveWorkflow] =
        useState<ActionWorkflowDefinition | null>(null);
    const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
    const [mode, setModeState] = useState<IntelligenceMode>(readStoredMode);
    // Bumped on every open*() call so the drawer can tell a fresh open request
    // apart from a re-render, without us having to remount it.
    const [openRequestId, setOpenRequestId] = useState(0);

    const setMode = useCallback((next: IntelligenceMode) => {
        setModeState(next);
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        window.localStorage.setItem(MODE_STORAGE_KEY, mode);
    }, [mode]);
    const [activeAgent, setActiveAgent] = useState<AgentIdentity | null>(null);

    const openDrawer = useCallback((agent?: AgentIdentity) => {
        setActiveWorkflow(null);
        setPendingPrompt(null);
        setActiveAgent(agent ?? null);
        setOpenRequestId((id) => id + 1);
        setIsOpen(true);
    }, []);

    const openWorkflow = useCallback((definition: ActionWorkflowDefinition) => {
        setActiveWorkflow(definition);
        setPendingPrompt(null);
        setActiveAgent(definition.agent ?? null);
        setOpenRequestId((id) => id + 1);
        setIsOpen(true);
    }, []);

    const openWithPrompt = useCallback((prompt: string, agent?: AgentIdentity) => {
        setActiveWorkflow(null);
        setPendingPrompt(prompt);
        setActiveAgent(agent ?? null);
        setOpenRequestId((id) => id + 1);
        setIsOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
        setIsOpen(false);
    }, []);

    useEffect(() => {
        if (consumeIntelligenceDrawerOnLoadRequest()) {
            openDrawer();
        }
    }, [openDrawer]);

    return (
        <BentoIntelligenceContext.Provider
            value={{
                openDrawer,
                openWorkflow,
                openWithPrompt,
                closeDrawer,
                isOpen,
                mode,
                setMode
            }}
        >
            {children}
            {/* Kept mounted for simpler open-request handoff. Drawer content now
                resets on close inside ParIntelligenceDrawer. */}
            <ParIntelligenceDrawer
                isOpen={isOpen}
                openRequestId={openRequestId}
                initialWorkflow={activeWorkflow}
                initialPrompt={pendingPrompt}
                initialAgent={activeAgent}
                onClose={closeDrawer}
            />
        </BentoIntelligenceContext.Provider>
    );
};

/** Use inside any Bento component to open the PAR Intelligence drawer. */
export function useBentoIntelligence(): BentoIntelligenceContextValue {
    const context = useContext(BentoIntelligenceContext);
    if (!context) {
        throw new Error(
            "useBentoIntelligence must be used within BentoIntelligenceProvider"
        );
    }
    return context;
}
